import { Injectable, computed, inject, signal } from '@angular/core';
import { BaseUserService } from '../services/base.service';
import { IAuthResp } from '../@ngrx/auth/auth.model';
import { lastValueFrom, tap } from 'rxjs';
import { AuthStore, getUserOwner } from '../@ngrx/auth/auth.reducer';
import { Store } from '@ngrx/store';
import { StorageService } from '../services/storage.service';
import { Actions } from '@ngrx/effects';
import { UsersService } from '../../../../admin/src/app/dashboard/set-ups/users/users.service';
import { EUserType, IUser } from '../@ngrx/user/user.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserActivityEndpointService } from '../components/user-activity/ua-extras/user-activity-endpoint.service';
import { EUA, UserActivity } from '../components/user-activity/ua-extras/user-activity.model';
import { UtilityService } from '../services/utility.service';
import { EVFunctions } from 'ets-fe-ng-sdk';
import { environment } from '../../environments/environment';

export type PublicPortalPersona = 'candidate' | 'applicant' | 'both';
export type PublicPortalSection = 'agent' | 'applications';
const PUBLIC_PORTAL_PROTOTYPE_PERSONA_KEY = 'publicPortalPrototypePersona';

export interface IForgotPasswordRequest {
  emailOrUsername: string;
  password: string;
  confirmPassword: string;
}

export interface IUserVerification {
  loginUrl?: string | null;
  passwordAuthUrl: string;
  username?: string | null;
  email?: string | null;
}

export interface ISystemUser {
  active: boolean;
  email: string;
  firstName: string;
  loginUrl: string;
  surname: string;
  username: string;
  verificationToken: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService extends BaseUserService {
  protected override baseURL = 'v1/authentication/';
  public uS = inject(UtilityService);
  protected readonly store = inject(Store);
  protected readonly actions = inject(Actions);
  readonly userS = inject<UsersService<IUser>>(UsersService<IUser>);
  protected readonly storageService = inject(StorageService);
  readonly userActivityEndpointService = inject(UserActivityEndpointService);
  readonly authObj = this.store.selectSignal(AuthStore.Selectors.authObj);
  readonly token = this.store.selectSignal(AuthStore.Selectors.token);
  readonly isLoggedIn = this.store.selectSignal(AuthStore.Selectors.isLoggedIn);
  readonly user = this.store.selectSignal(AuthStore.Selectors.user);
  readonly userGroup = this.store.selectSignal(AuthStore.Selectors.userGroup);
  readonly approvedForPlatform = this.store.selectSignal<boolean>(AuthStore.Selectors.approvedForPlatform);
  readonly userType = this.store.selectSignal(AuthStore.Selectors.userType);
  readonly checkableUserTypes = this.store.selectSignal(AuthStore.Selectors.checkableUserTypes);

  readonly userFullName = computed(() => {
    const u = this.user();
    return u ? u.fullName || EVFunctions.strConcatenator2(u.firstName, u.lastName, ' ') : null;
  });
  readonly restrictedAccess = computed(() => this.user()?.restrictedAccess == true);
  readonly isSuperAdmin = computed(() => this.userType() == EUserType.superAdmin);
  readonly isAdmin = computed(() => this.userType() == EUserType.admin);
  readonly isNia = computed(() => this.userType() == EUserType.nia);
  readonly isCiin = computed(() => this.userType() == EUserType.ciin);
  readonly isBpp = computed(() => this.userType() == EUserType.bpp);
  readonly isCompany = computed(() => this.userType() == EUserType.company);
  readonly isCifm = computed(() => this.userType() == EUserType.cifm);
  readonly isARIAN = computed(() => this.userType() == EUserType.arian);
  readonly isSuperAdminOrAdmin = computed(() => this.isSuperAdmin() || this.isAdmin());
  readonly isCIFMOrAdmin = computed(() => this.isAdmin() || this.isCifm());
  readonly isCompanyOrCIFMOrAdmin = computed(() => this.isAdmin() || this.isCifm() || this.isCompany());
  readonly isCIINOrAdmin = computed(() => this.isAdmin() || this.isCiin());
  readonly canAccessCIFM = computed(
    () => this.userType() == EUserType.admin || this.userType() == EUserType.cifm,
    // this.userType() == EUserType.nia,
  );
  readonly canAccessIntermediary = computed(
    () =>
      this.userType() == EUserType.admin ||
      this.userType() == EUserType.company ||
      this.userType() == EUserType.nia,
  );
  readonly canAccessCIIN = computed(
    () => this.userType() == EUserType.admin || this.userType() == EUserType.ciin,
  );
  readonly canAccessBpp = computed(
    () => this.userType() == EUserType.admin || this.userType() == EUserType.bpp,
  );
  readonly canAccessARIAN = computed(
    () => this.userType() == EUserType.admin || this.userType() == EUserType.arian,
  );
  readonly userCompany = this.store.selectSignal(AuthStore.Selectors.userCompany);
  readonly hasUserCompany = computed(() => !!this.userCompany());
  readonly userCompany$ = this.store.select(AuthStore.Selectors.userCompany);
  readonly userCompanyCode = computed(() => this.user()?.company || this.userCompany()?.companyCode);
  readonly userLicenseCategory = computed(() => this.user()?.licenseCat);
  readonly userLicenseCode = computed(() => this.user()?.licenseCode);
  readonly userCompanyCodeDescription = this.store.selectSignal(
    AuthStore.Selectors.userCompanyCodeDescription,
  );

  getUserOwner(user: Partial<IUser>) {
    return getUserOwner(user);
  }

  readonly candidate$ = this.store.select(AuthStore.Selectors.candidate);
  readonly candidate = toSignal(this.candidate$);
  readonly applicant$ = this.store.select(AuthStore.Selectors.applicant);
  readonly applicant = toSignal(this.applicant$);
  readonly publicPortalIdentityResolved = this.store.selectSignal(
    AuthStore.Selectors.publicPortalIdentityResolved,
  );
  readonly publicPortalIdentityError = this.store.selectSignal(AuthStore.Selectors.publicPortalIdentityError);
  private readonly publicPortalPrototypePersona = signal<PublicPortalPersona | null>(
    !environment.production ? this.getPrototypePersonaFromQuery() : null,
  );
  private readonly publicPortalPrototypeActive = signal(false);
  readonly publicPortalSection = signal<PublicPortalSection | null>(null);
  readonly publicPortalPrototypeEnabled = !environment.production;
  readonly publicPortalIdentityReady = computed(
    () => this.publicPortalPrototypeActive() || this.publicPortalIdentityResolved(),
  );
  readonly publicPortalPersona = computed<PublicPortalPersona>(() => {
    const prototypePersona = this.publicPortalPrototypeActive() ? this.publicPortalPrototypePersona() : null;
    if (prototypePersona) return prototypePersona;

    const hasCandidate = !!this.candidate();
    const hasApplicant = !!this.applicant();

    if (hasCandidate && hasApplicant) return 'both';
    if (hasApplicant) return 'applicant';
    return 'candidate';
  });
  readonly candidateRegDocuments = this.store.selectSignal(
    AuthStore.Selectors.candidateRegistrationDocuments,
  );
  readonly allowCandidateUpdate = this.store.selectSignal(AuthStore.Selectors.allowCandidateUpdate);
  readonly agent$ = this.store.select(AuthStore.Selectors.agent);
  readonly agent = toSignal(this.agent$);

  constructor() {
    // public userS: UsersService, // public candS: CandidateService,
    super();
    // effect(()=>{
    //   if(this.user)
    // })
  }

  getFromLocal() {
    this.store.dispatch(AuthStore.actions.getFromLocalAuthObj());
    this.store.dispatch(AuthStore.actions.getFromLocalCandidate());
    this.store.dispatch(AuthStore.actions.getFromLocalUser());
    this.store.dispatch(AuthStore.actions.getFromLocalUserGroup());
    this.store.dispatch(AuthStore.actions.getFromLocalCandidateAgent());
  }

  refreshCandidate() {
    this.store.dispatch(AuthStore.actions.getFromOnlineCandidate({}));
  }
  refreshApplicant(email?: string) {
    this.store.dispatch(AuthStore.actions.getFromOnlineApplicant({ email }));
  }
  getFromOnline() {
    if (environment.isCandidate) {
      this.store.dispatch(AuthStore.actions.getFromOnlinePublicPortalIdentities({}));
    } else {
      this.store.dispatch(AuthStore.actions.getFromOnlineUser());
    }
    //     .search({ fullname: authObj.firstName })
    //     .pipe(map((r) => r.content.find((x) => x.personalEmail == authObj.email)))
    //     .subscribe((candidate) => {
    //       if (!candidate) this.logout();
    //       else {
    //         this.store.dispatch(AuthStore.actions.setCandidate({ response: candidate }));
    //         this.saveToLocal();
    //       }
    //     });
    // else
    //   this.userS
    //     .fetchAll({ userName: authObj.email })
    //     .pipe(
    //       map((r) => r.content.find((x) => x.userEmail == authObj.email)),
    //       mergeMap((u) =>
    //         u
    //           ? of(u)
    //           : this.userS
    //               .fetchAll({ userName: authObj.email?.split('@')[0] })
    //               .pipe(map((r) => r.content.find((x) => x.userEmail == authObj.email))),
    //       ),
    //     )
    //     .subscribe((user) => {
    //       // debugger;
    //       if (!user) this.logout();
    //       else {
    //         this.store.dispatch(AuthStore.actions.setUser({ response: user }));
    //         this.saveToLocal();
    //       }
    //     });
  }

  sendMailVerificationOTP = (email: string) =>
    this.post<IUserVerification>({}, `notification/verify-email?email=${email}`).pipe();

  updatePassword = (data: IForgotPasswordRequest) => this.put<ISystemUser>(data, 'password');

  processForgotPasswordBeforeLoginAndSendNotification = (data: IUserVerification) =>
    this.post<IUserVerification>(data, 'pre-login/forgot-password/notification');

  validateReferenceBeforeLogin = (reference: string) =>
    this.get<IUserVerification>(`pre-login/validate/reference/${reference}`);

  updatePasswordBeforeLogin = (reference: string, data: IForgotPasswordRequest) =>
    this.put<ISystemUser>(data, `pre-login/password/${reference}`);

  private acceptAuthResponse(r: IAuthResp) {
    this.store.dispatch(AuthStore.actions.setAuthObj({ response: r }));
    this.store.dispatch(AuthStore.actions.saveToLocalAuthObj({ response: r }));
    this.getFromOnline();
    setTimeout(() => {
      lastValueFrom(
        this.userActivityEndpointService.submitUA([
          new UserActivity({
            screen: EUA.loggedIn,
          }),
        ]),
      );
    }, 500);
  }

  login = (data: { username: string; password: string }) =>
    this.post<IAuthResp>(data, 'login').pipe(
      tap((r) => {
        this.acceptAuthResponse(r);
      }),
    );

  logout = () => {
    this.store.dispatch(AuthStore.actions.logout({}));
  };

  getUserObject = async (auth: IAuthResp, type: 0 | 1 | 2 = 0): Promise<IUser | undefined> => {
    const sr = await lastValueFrom(
      this.userS.fetchAll({
        userName: type == 0 ? auth.userDetails.username : type == 1 ? auth.email : auth.email?.split('@')[0],
      }),
    );
    const user = sr.content?.find((x) => x.userEmail?.toLowerCase() == auth?.email?.toLowerCase());
    if (user || type >= 2) return user;
    else return await this.getUserObject(auth, (type + 1) as any);
  };
  fetchAgent() {
    this.store.dispatch(AuthStore.actions.getFromOnlineCandidateAgent());
  }
  fetchCandidateDocuments() {
    this.store.dispatch(AuthStore.actions.getFromOnlineCandidateRegDocuments());
  }

  setPublicPortalPrototypePersona(persona: PublicPortalPersona) {
    if (!this.publicPortalPrototypeEnabled) return;
    this.publicPortalPrototypePersona.set(persona);
    globalThis.sessionStorage?.setItem(PUBLIC_PORTAL_PROTOTYPE_PERSONA_KEY, persona);
  }

  setPublicPortalPrototypeActive(active: boolean) {
    this.publicPortalPrototypeActive.set(this.publicPortalPrototypeEnabled && active);
  }

  setPublicPortalSection(section: PublicPortalSection | null) {
    this.publicPortalSection.set(section);
  }

  private getPrototypePersonaFromQuery(): PublicPortalPersona | null {
    const queryPersona = new URLSearchParams(globalThis.location?.search || '').get('portalPersona');
    const storedPersona = globalThis.sessionStorage?.getItem(PUBLIC_PORTAL_PROTOTYPE_PERSONA_KEY);
    const persona = queryPersona || storedPersona;
    if (persona !== 'candidate' && persona !== 'applicant' && persona !== 'both') return null;
    if (queryPersona) globalThis.sessionStorage?.setItem(PUBLIC_PORTAL_PROTOTYPE_PERSONA_KEY, persona);
    return persona;
  }

  // requestReset = (username: string ) =>
  //   this.get<IAuthResp>(data, 'login').pipe(
  //     tap((r) => {
  //       this.store.dispatch(AuthStore.actions.loginSuccess({ response: r }));
  //       this.storageService.saveItem(this.authLSKey, r);
  //     }),
  //   );
}
